"use client";

import { useEffect } from 'react';
import posthog from 'posthog-js';
import { usePathname } from 'next/navigation';
import { isTest, isProd, isDev } from '@/config/domains';

const POSTHOG_API_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_API_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

export default function PostHogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();

  // Initialize PostHog with conditional feature enablement
  // Use requestIdleCallback to defer initialization, avoiding blocking first paint
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isLocalDev = isDev();
    const isTestEnv = isTest();
    const isProdEnv = isProd();

    if (!POSTHOG_API_KEY) {
      console.warn('[PostHog] NEXT_PUBLIC_POSTHOG_KEY not configured');
      return;
    }

    if (isTestEnv || isProdEnv) {
      posthog.init(POSTHOG_API_KEY, {
        api_host: POSTHOG_API_HOST,
        defaults: '2025-11-30',
        capture_pageview: false,
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: false,
          maskInputOptions: {
            password: true,
          },
          captureCanvas: {
            recordCanvas: true,
            canvasFps: 2,
            canvasQuality: "0.2",
          }
        }
      });
      console.log(`[PostHog] Initialized with ${isTestEnv ? 'test' : 'production'} environment config`);
    } else if (isLocalDev) {
      console.log('[PostHog] Local dev environment, PostHog not enabled');
    }
  }, []); // No dependencies, initialize only once

  // Track page views
  useEffect(() => {
    if (!pathname || typeof window === 'undefined') return;

    posthog.capture('$pageview', {
      current_url: window.location.href,
      path: pathname,
    });
  }, [pathname]);

  return children;
} 
